import { Controller } from '@hotwired/stimulus';
import {
  parseFormData,
  restructureVatData,
  postOnboardingData,
} from '../utils/onboarding_form_helpers';

export default class extends Controller {
  static targets = [
    'searchInput',
    'searchResults',
    'createForm',
    'contractForm',
    'searchPanel',
    'createPanel',
    'searchSubmit',
    'createSubmit',
    'submitButton',
    'loadingIndicator',
  ];
  static values = {
    sessionId: Number,
    selectedContractorId: Number,
    mode: { type: String, default: 'search' },
  };

  connect() {
    this.searchTimeout = null;
    this.selectedContractorData = null;
    this._hookModalOpen();
  }

  disconnect() {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    this._unhookModalOpen();
  }

  // Hook into the parent modal's open() to auto-reset state on each open
  _hookModalOpen() {
    const modalEl = this.element.querySelector('[data-controller="modal"]');
    if (!modalEl) return;

    this._modalEl = modalEl;
    this._originalModalOpen = null;

    const tryHook = () => {
      if (modalEl.modalController) {
        this._originalModalOpen = modalEl.modalController.open.bind(modalEl.modalController);
        modalEl.modalController.open = (...args) => {
          this.resetState();
          this._originalModalOpen(...args);
        };
      } else {
        // Modal controller not yet connected, retry on next frame
        this._hookFrame = requestAnimationFrame(tryHook);
      }
    };
    tryHook();
  }

  _unhookModalOpen() {
    if (this._hookFrame) cancelAnimationFrame(this._hookFrame);
    if (this._modalEl && this._modalEl.modalController && this._originalModalOpen) {
      this._modalEl.modalController.open = this._originalModalOpen;
    }
  }

  // Reset modal state (called when modal trigger button is clicked)
  resetState() {
    this.modeValue = 'search';
    this.selectedContractorIdValue = 0;
    this.selectedContractorData = null;

    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = '';
    }
    this.clearResults();
    this.updateSubmitButton();

    // Show default placeholder in search results
    if (this.hasSearchResultsTarget) {
      const placeholder = document.createElement('p');
      placeholder.className = 'text-sm text-neutral-400 py-4 text-center italic';
      placeholder.textContent = 'Saisissez un nom ou SIRET pour rechercher';
      this.searchResultsTarget.appendChild(placeholder);
    }
  }

  // Toggle to create form mode
  showCreateForm() {
    this.modeValue = 'create';
  }

  // Toggle back to search mode
  showSearchForm() {
    this.modeValue = 'search';
  }

  // Stimulus callback when mode value changes
  modeValueChanged() {
    if (!this.hasSearchPanelTarget || !this.hasCreatePanelTarget) return;

    if (this.modeValue === 'search') {
      this.searchPanelTarget.classList.remove('hidden');
      this.createPanelTarget.classList.add('hidden');
      if (this.hasSearchSubmitTarget) this.searchSubmitTarget.classList.remove('hidden');
      if (this.hasCreateSubmitTarget) this.createSubmitTarget.classList.add('hidden');
    } else {
      this.searchPanelTarget.classList.add('hidden');
      this.createPanelTarget.classList.remove('hidden');
      if (this.hasSearchSubmitTarget) this.searchSubmitTarget.classList.add('hidden');
      if (this.hasCreateSubmitTarget) this.createSubmitTarget.classList.remove('hidden');
    }
  }

  // Enable/disable the footer submit button based on mode and selection
  updateSubmitButton() {
    if (!this.hasSubmitButtonTarget) return;

    if (this.modeValue === 'search') {
      this.submitButtonTarget.disabled = !this.selectedContractorIdValue;
    }
  }

  // Debounced search for existing contractors
  search() {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);

    const query = this.searchInputTarget.value.trim();
    if (query.length < 2) {
      this.clearResults();
      return;
    }

    this.searchTimeout = setTimeout(() => this.performSearch(query), 300);
  }

  async performSearch(query) {
    // Show loading indicator
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.remove('hidden');
    }

    try {
      const response = await fetch(
        `/onboarding/${this.sessionIdValue}/search_contractors?q=${encodeURIComponent(query)}`,
        { headers: { Accept: 'application/json' } }
      );
      const data = await response.json();
      this.renderResults(data.contractors || []);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      // Hide loading indicator
      if (this.hasLoadingIndicatorTarget) {
        this.loadingIndicatorTarget.classList.add('hidden');
      }
    }
  }

  // Build radio-based search results
  renderResults(contractors) {
    this.clearResults();

    if (contractors.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'text-sm text-neutral-500 py-4 text-center';
      empty.textContent = 'Aucun résultat trouvé';
      this.searchResultsTarget.appendChild(empty);
      return;
    }

    contractors.forEach((contractor) => {
      // Label wrapping radio input
      const label = document.createElement('label');
      label.className =
        'relative flex items-start p-3 border border-neutral-200 rounded-lg hover:bg-neutral-50 cursor-pointer transition-colors';

      // Radio input
      const radio = document.createElement('input');
      radio.type = 'radio';
      radio.name = 'contractor_search_result';
      radio.value = contractor.id;
      radio.className =
        'mt-0.5 h-4 w-4 text-primary-600 border-neutral-300 focus-visible:ring-primary-500';
      radio.dataset.contractorId = contractor.id;
      radio.dataset.contractorName = contractor.contractor_name;
      radio.dataset.contractorSiret = contractor.siret || '';
      radio.dataset.contractorTrades = JSON.stringify(contractor.trades || []);
      radio.dataset.contractorEmail = contractor.email || '';
      radio.dataset.contractorPhone = contractor.phone || '';
      radio.dataset.action = 'change->onboarding-add#selectExisting';

      // Content div
      const content = document.createElement('div');
      content.className = 'ml-3 flex-1';

      const name = document.createElement('span');
      name.className = 'font-medium text-sm text-neutral-900';
      name.textContent = contractor.contractor_name;

      const siret = document.createElement('span');
      siret.className = 'text-xs text-neutral-500 ml-2 font-mono';
      siret.textContent = contractor.siret || '';

      const nameRow = document.createElement('div');
      nameRow.appendChild(name);
      nameRow.appendChild(siret);
      content.appendChild(nameRow);

      if (contractor.trades && contractor.trades.length > 0) {
        const trades = document.createElement('div');
        trades.className = 'text-xs text-neutral-400 mt-1';
        trades.textContent = contractor.trades.join(', ');
        content.appendChild(trades);
      }

      label.appendChild(radio);
      label.appendChild(content);
      this.searchResultsTarget.appendChild(label);
    });
  }

  clearResults() {
    if (!this.hasSearchResultsTarget) return;
    while (this.searchResultsTarget.firstChild) {
      this.searchResultsTarget.removeChild(this.searchResultsTarget.firstChild);
    }
  }

  // Handle radio selection: highlight label and store data
  selectExisting(event) {
    const radio = event.currentTarget;
    const label = radio.closest('label');

    // Remove highlight from all result labels
    this.searchResultsTarget.querySelectorAll('label').forEach((item) => {
      item.classList.remove('border-primary-300', 'bg-primary-50');
      item.classList.add('border-neutral-200');
    });

    // Highlight selected label
    if (label) {
      label.classList.remove('border-neutral-200');
      label.classList.add('border-primary-300', 'bg-primary-50');
    }

    this.selectedContractorIdValue = parseInt(radio.dataset.contractorId, 10);
    this.selectedContractorData = {
      contractor_name: radio.dataset.contractorName,
      siret: radio.dataset.contractorSiret,
      trades: JSON.parse(radio.dataset.contractorTrades || '[]'),
      email: radio.dataset.contractorEmail,
      phone: radio.dataset.contractorPhone,
    };

    this.updateSubmitButton();
  }

  // Unified submit — dispatches based on mode
  async submit(event) {
    event.preventDefault();

    if (this.modeValue === 'search') {
      await this.submitExisting();
    } else {
      await this.submitNew();
    }
  }

  // Submit existing contractor (from search results)
  async submitExisting() {
    if (!this.selectedContractorIdValue) return;

    await this.postAddItem('contractor', {
      ...this.selectedContractorData,
      _use_existing_contractor_id: this.selectedContractorIdValue,
      _source: 'manual',
    });
  }

  // Submit new contractor from creation form
  async submitNew() {
    const formData = new FormData(this.createFormTarget);
    const data = parseFormData(formData);
    data._source = 'manual';

    await this.postAddItem('contractor', data);
  }

  // Submit new contract from form (used by separate add-contract modal)
  async submitContract(event) {
    event.preventDefault();

    const formData = new FormData(this.contractFormTarget);
    const data = parseFormData(formData);
    data._source = 'manual';

    restructureVatData(data);

    await this.postAddItem('contract', data);
  }

  // POST add_item endpoint
  async postAddItem(section, data) {
    try {
      await postOnboardingData(`/onboarding/${this.sessionIdValue}/add_item`, 'POST', {
        section,
        data,
      });
    } catch (error) {
      console.error('Add item failed:', error);
    }
  }
}
